import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  inject,
} from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { NavItem } from '../../../../core/models/nav-item.model';
import {
  AccountType,
  AuthenticationService,
} from '../../../../shared/services/auth/authentication.service';
import { LayoutService } from '../../../../shared/services/layout/layout.service';
import { IconifyIconComponent } from '../../../../shared/components/iconify-icon/iconify-icon.component';
import { MyProfileModalComponent } from '../my-profile-modal/my-profile-modal.component';
import { HasAnyPermissionDirective } from '../../../../shared/directives/has-any-permission.directive';
import { map, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-top-nav',
  standalone: true,
  imports: [
    RouterLink,
    RouterLinkActive,
    IconifyIconComponent,
    MyProfileModalComponent,
    HasAnyPermissionDirective,
  ],
  templateUrl: './top-nav.component.html',
  styleUrl: './top-nav.component.scss',
})
export class TopNavComponent implements OnInit {
  private readonly el = inject(ElementRef);
  activeDropdown: string | null = null;
  mobileOpen = false;
  menu: NavItem[] = [];
  showMyProfileModal: boolean = false;

  private destroy$: Subject<void> = new Subject();

  constructor(
    private authenticationService: AuthenticationService,
    private layoutService: LayoutService,
  ) {}

  ngOnInit(): void {
    // Filter menu items based on permissions before rendering
    const filterByPermission = (items: NavItem[]): NavItem[] => {
      return items.filter((item) => {
        // Check parent permission
        const hasPermission =
          !item.permissions?.length ||
          this.authenticationService.hasAnyPermission(item.permissions);

        if (!hasPermission) return false;

        // Filter children recursively
        if (item.children?.length) {
          item.children = item.children.filter(
            (child) =>
              !child.permissions?.length ||
              this.authenticationService.hasAnyPermission(child.permissions),
          );
          // Only show parent if it has visible children or no children
          return item.children.length > 0 || !item.children;
        }

        return true;
      });
    };

    this.layoutService.menu
      .pipe(
        map((menu) => filterByPermission(menu)),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (menu) => {
          this.menu = menu;
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get accountType(): AccountType {
    return this.authenticationService.accountType;
  }

  get items(): NavItem[] {
    return this.menu;
  }

  get portalLabel(): string {
    return this.accountType === 'ADMIN' ? 'Admin Portal' : 'User Portal';
  }

  get username(): string {
    return this.authenticationService.username;
  }

  onMyProfile() {
    this.showMyProfileModal = true;
    this.closeDropdowns();
  }

  toggleDropdown(label: string): void {
    this.activeDropdown = this.activeDropdown === label ? null : label;
  }

  isDropdownOpen(label: string): boolean {
    return this.activeDropdown === label;
  }

  toggleMobile(): void {
    this.mobileOpen = !this.mobileOpen;
    this.activeDropdown = null;
  }

  closeDropdowns(): void {
    this.activeDropdown = null;
  }

  logout() {
    this.authenticationService.logout();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.el.nativeElement.contains(event.target)) {
      this.activeDropdown = null;
      this.mobileOpen = false;
    }
  }
}
